import { useState, useEffect, useRef, useCallback } from "react";
import { Send, MessageCircle } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useApi } from "../hooks/useApi";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export function Chat() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content: "Hi there! I'm here to listen and support you. How are you feeling today?",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const [animatingMessages, setAnimatingMessages] = useState<Set<string>>(new Set());
  const [allMessagesLoaded, setAllMessagesLoaded] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const apiClient = useApi();

  // Load conversation history when component mounts
  const { data: conversationsData } = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => apiClient.get("/api/v1/chat/conversations?status=active&limit=1"),
    enabled: !hasLoadedHistory,
  });

  // Load messages for the current conversation
  const { data: messagesData } = useQuery({
    queryKey: ["chat-messages", currentConversationId],
    queryFn: () => apiClient.get(`/api/v1/chat/messages?conversationId=${currentConversationId}`),
    enabled: !!currentConversationId,
  });

  // Chat API mutation
  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const response = await apiClient.post("/api/v1/ai/chat", {
        message,
        conversationId: currentConversationId,
      });
      return response.data;
    },
    onSuccess: (data) => {
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.message,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Add to animating messages for fade-in-up effect
      setAnimatingMessages((prev) => new Set(prev).add(assistantMessage.id));

      // Update conversation ID if we got one
      if (data.conversationId && !currentConversationId) {
        setCurrentConversationId(data.conversationId);
      }

      setIsTyping(false);
    },
    onError: (error) => {
      console.error("Chat error:", error);
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsTyping(false);
    },
  });

  // Load conversation history when data arrives
  useEffect(() => {
    if (conversationsData && !hasLoadedHistory) {
      const conversations = (conversationsData as any)?.data?.conversations || [];
      if (conversations.length > 0) {
        const latestConversation = conversations[0];
        setCurrentConversationId(latestConversation.id);
      }
      setHasLoadedHistory(true);
    }
  }, [conversationsData, hasLoadedHistory]);

  // Load messages when conversation is set - only latest user and assistant messages
  useEffect(() => {
    if (messagesData && currentConversationId) {
      const dbMessages = (messagesData as any)?.data?.messages || [];
      if (dbMessages.length > 0) {
        // Sort messages by created_at to find latest ones
        const sortedMessages = dbMessages.sort(
          (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        // Find the latest user and assistant messages
        const latestUserMessage = [...sortedMessages]
          .reverse()
          .find((msg: any) => msg.role === "user");
        const latestAssistantMessage = [...sortedMessages]
          .reverse()
          .find((msg: any) => msg.role === "assistant");

        const latestMessages: ChatMessage[] = [];

        if (latestUserMessage) {
          latestMessages.push({
            id: latestUserMessage.id,
            role: latestUserMessage.role,
            content: latestUserMessage.content,
            timestamp: new Date(latestUserMessage.created_at),
          });
        }

        if (latestAssistantMessage) {
          latestMessages.push({
            id: latestAssistantMessage.id,
            role: latestAssistantMessage.role,
            content: latestAssistantMessage.content,
            timestamp: new Date(latestAssistantMessage.created_at),
          });
        }

        // Sort by timestamp for chronological order
        latestMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        setMessages(latestMessages);

        // Animate the loaded messages
        const messageIds = new Set(latestMessages.map((msg) => msg.id));
        setAnimatingMessages(messageIds);

        // Reset loading states
        setAllMessagesLoaded(false);
        setIsLoadingOlder(false);
      }
    }
  }, [messagesData, currentConversationId]);

  // Gentle fade-in animation instead of auto-scroll for better UX in mental health context
  // Messages appear with fade-in-up animation rather than jarring scroll

  // Load older messages when scrolling up
  const loadOlderMessages = async () => {
    if (!currentConversationId || allMessagesLoaded || isLoadingOlder) return;

    setIsLoadingOlder(true);
    try {
      // Get all messages for this conversation to find older ones
      const response = await apiClient.get(
        `/api/v1/chat/messages?conversationId=${currentConversationId}`
      );
      const allDbMessages = (response as any)?.data?.messages || [];

      if (allDbMessages.length > 0) {
        // Sort all messages by created_at
        const sortedAllMessages = allDbMessages.sort(
          (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        // Find messages that are older than our current oldest message
        const currentOldestTimestamp = Math.min(...messages.map((m) => m.timestamp.getTime()));
        const olderMessages = sortedAllMessages.filter(
          (msg: any) => new Date(msg.created_at).getTime() < currentOldestTimestamp
        );

        if (olderMessages.length > 0) {
          // Take the most recent batch of older messages (last 10)
          const messagesToAdd = olderMessages.slice(-10).map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
          }));

          // Prepend older messages
          setMessages((prevMessages) => [...messagesToAdd, ...prevMessages]);

          // Animate the newly loaded messages
          const newMessageIds = new Set(messagesToAdd.map((msg) => msg.id));
          setAnimatingMessages((prev) => new Set([...prev, ...newMessageIds]));
        } else {
          setAllMessagesLoaded(true);
        }
      }
    } catch (error) {
      console.error("Failed to load older messages:", error);
    } finally {
      setIsLoadingOlder(false);
    }
  };

  // Handle scroll to load older messages
  const handleScroll = useCallback(
    (event: Event) => {
      const container = event.target as HTMLElement;
      if (container && container.scrollTop < 100 && !isLoadingOlder && !allMessagesLoaded) {
        loadOlderMessages();
      }
    },
    [isLoadingOlder, allMessagesLoaded]
  );

  // Add scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [currentConversationId, allMessagesLoaded, isLoadingOlder, handleScroll]);

  // Send message function
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: inputMessage.trim(),
      timestamp: new Date(),
    };

    // Add user message to local state immediately for better UX
    setMessages((prev) => [...prev, userMessage]);

    // Add to animating messages for fade-in-up effect
    setAnimatingMessages((prev) => new Set(prev).add(userMessage.id));

    setInputMessage("");
    setIsTyping(true);

    // Send to AI (which will save to database and return assistant response)
    chatMutation.mutate(inputMessage.trim());
  };

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-50 flex flex-col overflow-hidden">
      {/* Header - Fixed at top */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">Chat Support</h1>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <MessageCircle className="w-4 h-4" />
            <span>AI-Powered Support</span>
          </div>
        </div>
      </div>

      {/* Chat Container - Fixed Height with Internal Scrolling */}
      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full overflow-hidden">
        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {/* Loading older messages indicator */}
          {isLoadingOlder && (
            <div className="flex justify-center py-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
            </div>
          )}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} ${
                animatingMessages.has(message.id) ? "animate-fade-in-up" : ""
              }`}
              onAnimationEnd={() => {
                // Remove from animating messages after animation completes
                setAnimatingMessages((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(message.id);
                  return newSet;
                });
              }}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.role === "user"
                    ? "bg-red-600 text-white rounded-br-none"
                    : "bg-gray-100 text-gray-900 rounded-bl-none"
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                <p
                  className={`text-xs mt-1 ${
                    message.role === "user" ? "text-red-100" : "text-gray-500"
                  }`}
                >
                  {message.timestamp.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-900 rounded-lg rounded-bl-none px-4 py-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Disclaimer */}
        <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
          <p className="text-xs text-blue-800 text-center">
            <strong>Support:</strong> This chat provides emotional support. For mental health
            crises, contact professionals or emergency services.
          </p>
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex space-x-2">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={3}
              disabled={isTyping}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isTyping}
              className="self-end bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
